import { TableDataType } from '@spo/logic';
import {
  flexRender,
  getCoreRowModel,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import { VpCheckbox } from '@vtmn-play/react';
import { useMemo } from 'react';
import './tableView.scss';
import { TableViewRow } from './TableViewRow';
import { TableViewRowWithCheckbox } from './TableViewRowWithCheckbox';

type TableViewProps<T extends TableDataType> = {
  data: T[];
  columns: any;
  selectedIds?: string[];
  smId?: string;
  massEdit?: boolean;
  onCellClick?: (row: T) => void;
  selectId?: (id: string, selectedIds: string[]) => void;
  toggleAll?: (checkStatus: boolean) => void;
};

export const TableView = <T extends TableDataType>({
  data,
  columns,
  selectedIds,
  smId,
  massEdit = false,
  onCellClick,
  selectId,
  toggleAll,
}: TableViewProps<T>) => {
  const tableData = useReactTable<T>({
    data,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
  });

  const isAllChecked = useMemo(() => {
    const allModelIds = data?.map((model) => model.id);
    return (
      !!selectedIds?.length &&
      allModelIds.every((modelId) => selectedIds.includes(modelId))
    );
  }, [selectedIds, data]);

  return (
    <table data-testid={`table-view-${massEdit ? 'mass-edit' : smId}`}>
      <thead data-testid="table-view_header" className="table-view_header">
        {tableData.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {selectId && selectedIds && toggleAll && (
              <th className="cell" key="checkbox">
                <VpCheckbox
                  data-testid="select-all-model-checkbox"
                  id="select-all-model-checkbox"
                  checked={isAllChecked}
                  onChange={() => {
                    toggleAll(!isAllChecked);
                  }}
                />
              </th>
            )}
            {headerGroup.headers.map((header) => (
              <th key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody data-testid="table-view_body" className="table-view_body">
        {tableData
          .getRowModel()
          .rows.map((row: Row<T>) =>
            selectId && selectedIds ? (
              <TableViewRowWithCheckbox<T>
                key={row.original.id}
                row={row}
                selectId={selectId}
                selectedIds={selectedIds}
                onCellClick={onCellClick}
              />
            ) : (
              <TableViewRow<T>
                key={row.original.id}
                row={row}
                onCellClick={onCellClick}
              />
            ),
          )}
      </tbody>
    </table>
  );
};
